/**
 * Install instructions — there is no "Install" button that works everywhere.
 *
 * iOS Safari has no install prompt at all; the only way in is the Share
 * sheet's "Ana Ekrana Ekle", three taps deep and easy to never notice.
 * Android's Chrome can prompt natively, but showing the same plain steps
 * there too keeps this to one code path instead of two.
 */

import { useEffect, useId, useRef } from 'react';
import { Button } from './ui/Button.js';
import './InstallAppDialog.css';

function platform(): 'ios' | 'android' | 'desktop' {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

export function InstallAppDialog({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const device = platform();

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="installapp__backdrop" role="presentation">
      <div
        className="installapp__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 className="installapp__title" id={titleId}>
          Uygulamayı Yükle
        </h2>
        <p className="installapp__body">
          Trackpil&apos;i telefonunuzun ana ekranına ekleyin — bir kez yaptıktan sonra
          normal bir uygulama gibi açılır, tarayıcı çubuğu görünmez.
        </p>

        {device === 'ios' && (
          <ol className="installapp__steps">
            <li>
              Safari&apos;de altta ortadaki <strong>Paylaş</strong> ikonuna dokunun (kare,
              üzerinde yukarı ok).
            </li>
            <li>
              Açılan listede <strong>&quot;Ana Ekrana Ekle&quot;</strong>&apos;yi bulun.
            </li>
            <li>
              Sağ üstteki <strong>Ekle</strong>&apos;ye dokunun.
            </li>
          </ol>
        )}

        {device === 'android' && (
          <ol className="installapp__steps">
            <li>
              Chrome adres çubuğunun sağındaki <strong>⋮</strong> menüsüne dokunun.
            </li>
            <li>
              <strong>&quot;Ana ekrana ekle&quot;</strong> veya{' '}
              <strong>&quot;Uygulamayı yükle&quot;</strong>&apos;yi seçin.
            </li>
            <li>Onaylayın — ikon ana ekrana eklenir.</li>
          </ol>
        )}

        {device === 'desktop' && (
          <ol className="installapp__steps">
            <li>
              Chrome veya Edge&apos;de adres çubuğunun sağındaki yükleme ikonuna tıklayın
              (yoksa sağ üst menüden <strong>&quot;Uygulamayı Yükle&quot;</strong>
              &apos;yi arayın).
            </li>
            <li>Açılan pencerede onaylayın.</li>
          </ol>
        )}

        <div className="installapp__actions">
          <Button type="button" variant="secondary" onClick={onClose} ref={closeRef}>
            Kapat
          </Button>
        </div>
      </div>
    </div>
  );
}
